import { axisBottom, axisTop, axisLeft } from "d3-axis";
import { scaleLinear, scalePow, scaleBand } from "d3-scale";
import { select, selectAll } from "d3-selection";
import { min, max, extent } from "d3-array";
import { forceSimulation, forceX, forceY, forceCollide } from "d3-force";
import { Delaunay, renderCell } from "d3-delaunay";
import { voronoi } from "d3-voronoi";
import positionDict from "../data/positionDict.json";

export default class makeChart {

  constructor(opts) {
    Object.assign(this, opts);

    this.appendElements();
    this.update();
  }

  update() {
    this._setDimensions();
    this._setScales();
    this.render();
  }

  _setDimensions() {
    this.mobile = window.innerWidth <= 599 ? true : false;
    this.highlight = this.mobile ? "mobile_highlight" : "highlight";

    this.margin = {
      top: this.mobile ? 120 : 90,
      right: this.mobile ? 25 : 50,
      bottom: 0,
      left: this.mobile ? 13 : 135,
    };

    this.width = this.element.offsetWidth - this.margin.left - this.margin.right;
    this.height = this.element.offsetHeight - this.margin.top - this.margin.bottom;
  }

  _setScales() {
    this.xScale = scaleLinear()
      .rangeRound([0, this.width])
      .domain([-5,20])
      .clamp(true);

    this.yScale = scaleBand()
      .rangeRound([this.height, 0])
      .domain(["Transportation", "Housing", "Food", "Healthcare", "Other"].reverse())

    var _this = this // To deal with "this" scoping

    this.simulation = forceSimulation(this.data)
      .force("x", forceX(function(d) { return _this.xScale(d.change); }).strength(1))
      .force("y", forceY(function(d) { return _this.yScale(d.parent); }))
      .force("collide", forceCollide(function(d) {return 10 + 1; }))
      .stop();

    for (var i = 0; i < 1000; ++i) this.simulation.tick();

    // this.delaunay = Delaunay.from(this.data, d=> d.x, d=> d.y);
    // this.voronoi = this.delaunay.voronoi([-this.margin.top/2, -this.margin.left/2, this.width + this.margin.right, this.height]);

    this.voronoiGenerator = voronoi()
      .x(d=> d.x)
      .y(d=> d.y)
      .extent([[-this.margin.top/2,-this.margin.left/2], [this.width+this.margin.right, this.height]]);

  }

  appendElements() {
    this.mobile = window.innerWidth <= 599 ? true : false;
    select(".chart").style("height", this.mobile ? "700px" : "600px");

    this.svg = select(this.element).append("svg");

    this.interactionRect = this.svg.append("rect").classed("interaction-rect", true);

    this.plot = this.svg.append("g").attr("class", "chart-g");

    this.increasedText = this.plot.append("text").classed("increased-text", true)
      .text("Increased ⟶");

    this.xAxis = this.plot.append("g").classed("axis x-axis", true);

    this.avgLine = this.plot.append("line").classed("average-line", true);

    this.avgText = this.plot.append("text").classed("average-text", true)
      .html("<tspan>US total</tspan><tspan>+6.8%</tspan>")

    this.catContainer = this.plot.append("g").classed("cat-container", true);

    this.categoryGroups = this.catContainer.selectAll(".cat-group")
      .data([...new Set(this.data.map(d=> d.parent))])
      .enter()
      .append("g")
      .attr("class", d=> `${slugify(d)} cat-group`);

    this.catLabel = this.categoryGroups.append("text")
      .text(d=> d)

    this.catLine = this.categoryGroups.append("line");

    this.swarmContainer = this.plot.append("g").classed("swarm-container", true);

    this.swarmGroup = this.swarmContainer.selectAll(".swarm-group")
      .data(this.data)
      .enter()
      .append("g")
      .attr("class", d=> `${slugify(d.item)} ${slugify(d.parent)} swarm-group`)

    this.swarmDot = this.swarmGroup.append("circle")
      .attr("class", "swarm-dot")

    this.swarmText = this.swarmGroup.append("text")
      .text(d=> d.shorter_name)
      .attr("class", "swarm-text")

    this.swarmValue = this.swarmGroup.append("text")
      .text(d=> d.change == 0 ? `+0%` : d.change > 0 ? `+${d.change.toFixed(1)}%` : `${d.change.toFixed(1)}%`)
      .attr("class", "swarm-value")

    this.voronoiContainer = this.plot.append("g").attr("class", "voronoi-container");

  }

  render() {
    iframeMessenger.resize()
    select(".chart")
      .style("height", this.mobile ? "650px" : "600px")
      .style("padding-top", this.mobile ? 0 : "15px")

    this.svg
      .attr("width", this.width + this.margin.left + this.margin.right)
      .attr("height", this.height + this.margin.top + this.margin.bottom);

    this.plot
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    this.interactionRect
      .on("click", (d,e) => {
        this.swarmGroup.style("opacity", 1);
        console.log("clicking on interation rect")
      })
      .attr("fill", "white")
      .attr("fill-opacity", 0)
      .attr("x", -this.margin.left)
      .attr("y", 0)
      .attr("width", this.width+this.margin.right+this.margin.left)
      .attr("height", this.height+this.margin.bottom)

    this.increasedText
      .attr("x", this.xScale(0) - 5)
      .attr("y", -this.margin.top + 25)
      .style("display", this.mobile ? "none" : "block")

    let topPad = this.mobile ? 60 : 50;
    this.xAxis
      .attr("transform", "translate(0," + (-this.margin.top + topPad) + ")")
      .call(axisTop(this.xScale)
          .tickSize(-this.height)
          .tickFormat(d=> d == 20 ? `+${d}% or more` : d == 0 ? d : d > 0 ? `+${d}%` :`${d}%`)
          .tickValues([-5,0,5,10,15,20])
      );

    selectAll(".x-axis .tick text")
      .filter(f=> f == 20)
      .html("<tspan>+20%</tspan><tspan>or more</tspan>")
      .attr("y", -33)

    selectAll(".x-axis tspan")
      .attr("x", 0)
      .attr("dy", 15)

    this.avgLine
      .attr("x1", this.xScale(6.8))
      .attr("x2", this.xScale(6.8))
      .attr("y1", this.mobile ? -this.margin.top/2 - 20 : -this.margin.top/2 - 15)
      .attr("y2", this.height-(this.margin.top/2)+5)

    this.avgText
      .attr("y", this.mobile ? -this.margin.top/2 - 58 : -this.margin.top/2 - 54)
      .attr("text-anchor", "middle")

    selectAll(".average-text tspan")
      .attr("dy", 17)
      .attr("x", this.xScale(6.8))
  
    this.categoryGroups
      .attr("transform", d=> `translate(${-this.margin.left}, ${this.yScale(d)})`)

    this.catLabel
      .attr("y", this.mobile ? -30 : 3)
      .attr("x", this.mobile ? 17 : 0)

    this.catLine
      .attr("x1", this.margin.left)
      .attr("y1", 0)
      .attr("x2", this.width + this.margin.left + this.margin.right)
      .attr("y2", 0)

    this.swarmGroup
      .attr("transform", d=> `translate(${d.x}, ${d.y})`)

    this.swarmGroup.filter(d=> d[this.highlight] == 1).raise();

    this.swarmDot
      .attr("r", 10)
      .filter(d=> d[this.highlight] == 1)
      .classed("active", true);

    this.swarmText
      .attr("x", d=> {
        let position = positionDict[d.shorter_name];
        if (this.mobile && d.change < 3) {
          return -15
        }
        return position == "left" ? -15 : 0;
      })
      .attr("y", d=> {
        let position = positionDict[d.shorter_name];
        return position == "down" ? 22 : position == "left" ? 4 : -15;
      })
      .attr("text-anchor", d=> {
        let position = positionDict[d.shorter_name];
        if (this.mobile && d.change < 3) {
          return "start"
        }
        return position == "left" ? "end" : "middle";
      })
      .style("display", d=> d[this.highlight] == 1.0 ? "block" : "none");

    this.swarmValue
      .attr("x", 0)
      .attr("y", d=> {
        let position = positionDict[d.shorter_name];
        return position == "down" ? -15 : 28;
      })
      .attr("text-anchor", "middle")

    this.voronoiContainer.html("")
    if (!this.mobile) {
      this.voroniCells = this.voronoiContainer.selectAll('.voronoi-cell')
        .data(this.voronoiGenerator(this.data).polygons())
        .enter()
        .append("path")
        .attr("d", d => {
          return d ? "M" + d.join("L") + "Z" : "";
        })
        .style("fill", "white")
        .style("fill-opacity", 0)
        .on("mouseover", (e,d)=> {
          this.swarmGroup.filter(f=> f.shorter_name != d.data.shorter_name).style("opacity", 0.2);
          selectAll(".swarm-group .swarm-text").style("display", "none");
          selectAll(".swarm-dot.active").classed("active", false);
          select(`.swarm-group.${slugify(d.data.item)}`).raise().classed("active", true)
          select(`.swarm-group.${slugify(d.data.item)} .swarm-text`).style("display", "block");
          select(`.swarm-group.${slugify(d.data.item)} .swarm-value`).style("display", "block");
        })
        .on("mouseleave", (e,d)=> {
          this.swarmGroup.style("opacity", 1);
          select(`.swarm-group.${slugify(d.data.item)}`).lower().classed("active", false)
          select(`.swarm-group.${slugify(d.data.item)} .swarm-text`).style("display", "none");
          select(`.swarm-group.${slugify(d.data.item)} .swarm-value`).style("display", "none");
  
          this.swarmText
            .style("display", d=> d[this.highlight] == 1.0 ? "block" : "none");
  
          this.swarmDot
            .filter(d=> d[this.highlight] == 1)
            .classed("active", true);
  
          this.swarmGroup.filter(d=> d[this.highlight] == 1.0).raise()
        })
    }

    if (this.mobile) {
      this.swarmGroup
        .on("mouseover", (e,d)=> {
          this.swarmGroup.filter(f=> f.shorter_name != d.shorter_name).style("opacity", 0.2);
          selectAll(".swarm-group .swarm-text").style("display", "none");
          selectAll(".swarm-dot.active").classed("active", false);
          select(`.swarm-group.${slugify(d.item)}`).raise().classed("active", true)
          select(`.swarm-group.${slugify(d.item)} .swarm-text`).style("display", "block");
          select(`.swarm-group.${slugify(d.item)} .swarm-value`).style("display", "block");
        })
        .on("mouseleave", (e,d)=> {
          this.swarmGroup.style("opacity", 1);
          select(`.swarm-group.${slugify(d.item)}`).lower().classed("active", false)
          select(`.swarm-group.${slugify(d.item)} .swarm-text`).style("display", "none");
          select(`.swarm-group.${slugify(d.item)} .swarm-value`).style("display", "none");
  
          this.swarmText
            .style("display", d=> d[this.highlight] == 1.0 ? "block" : "none");
  
          this.swarmDot
            .filter(d=> d[this.highlight] == 1)
            .classed("active", true);
  
          this.swarmGroup.filter(d=> d[this.highlight] == 1.0).raise()
        })
    }

    select(".headline")
      .text("How prices changed for individual items")

    select(".standfirst")
      .text("Change in price for select items in the US from November 2020 to November 2021")

    select(".source")
      .text("Guardian graphic. Source: Consumer Price Index, US Bureau of Labor Statistics")
  }
}

function slugify(text) {
    return text.toString().toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
}

function formatNumber(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}